import { useMemo } from 'react';

import { getSearchTextMatchPriority, SEARCH_TEXT_MATCH_PRIORITY } from '@kc-console/core';

import { ALL_CATEGORY } from '../stores';

import type { MegaMenuCategory, MegaMenuService, Menus, TokenScope } from '@kc-console/context';

export type SearchMenu = {
  category: string;
  id: string;
  name: string;
  path: string;
  scope: TokenScope;
  serviceKeyword: string;
};

const pushSearchMenu = (
  searchMenus: SearchMenu[],
  menu: Menus,
  parentMenuName: string,
  category: MegaMenuCategory,
  service: MegaMenuService,
) => {
  const menuName = [parentMenuName, menu.name].join(' > ');
  searchMenus.push({
    name: menuName,
    id: menu.keyword,
    category: category.name,
    path: menu.path,
    serviceKeyword: service.keyword,
    scope: menu.scope,
  });

  if (menu.subMenus?.length > 0) {
    menu.subMenus.forEach(subMenu =>
      pushSearchMenu(searchMenus, subMenu, menuName, category, service),
    );
  }
};

const transformServices = (categories: MegaMenuCategory[]) => {
  return categories.reduce<SearchMenu[]>((acc, category) => {
    category.services.forEach(service => {
      acc.push({
        name: service.name,
        id: service.keyword,
        category: category.name,
        path: service.path,
        serviceKeyword: service.keyword,
        scope: service.scope,
      });

      if (service.menus?.length > 0) {
        service.menus.forEach(menu => pushSearchMenu(acc, menu, service.name, category, service));
      }
    });
    return acc;
  }, []);
};

/** 이름 매칭이 없을 때만 카테고리 매칭을 사용하며, 카테고리는 원본과 동일하게 3·4단계 가중치를 둔다. */
const mapCategoryMatchPriority = (priority: number): number => {
  if (!Number.isFinite(priority)) return SEARCH_TEXT_MATCH_PRIORITY.NO_MATCH;
  return priority === SEARCH_TEXT_MATCH_PRIORITY.INCLUDES ? 4 : 3;
};

const getSearchMatchScore = (menu: SearchMenu, keyword: string): number => {
  const namePriority = getSearchTextMatchPriority(menu, m => m.name, keyword);
  if (Number.isFinite(namePriority)) {
    return namePriority;
  }
  const categoryPriority = getSearchTextMatchPriority(menu, m => m.category, keyword);
  return mapCategoryMatchPriority(categoryPriority);
};

export const useSearchedMenus = (
  allCategories: MegaMenuCategory[],
  keyword: string,
  category: string,
) => {
  const transformedServices = useMemo(() => transformServices(allCategories), [allCategories]);

  if (keyword === '') return [];

  return transformedServices
    .filter(menu => !category || category === ALL_CATEGORY || category === menu.category)
    .map(menu => ({ menu, score: getSearchMatchScore(menu, keyword) }))
    .filter(({ score }) => isFinite(score))
    .sort((a, b) => a.score - b.score)
    .map(({ menu }) => menu);
};
